import SlsTracker from '@aliyun-sls/web-track-browser'
import createStsPlugin from '@aliyun-sls/web-sts-plugin'
import { useUserStore } from '@/store/user.store'
import { hasPrivacyConsent } from '@/utils/privacy-consent'
import type {
  RouteLocationNormalizedGeneric,
  RouteLocationNormalizedLoadedGeneric
} from 'vue-router'
import { dayjs } from 'element-plus'
import { router } from '@/router'

const opts = {
  host: 'cn-shenzhen.log.aliyuncs.com', // 所在地域的服务入口。例如cn-hangzhou.log.aliyuncs.com
  project: 'yt-webtracking', // Project名称。
  logstore: 'pte-web', // Logstore名称。
  time: 10, // 发送日志的时间间隔，默认是10秒。
  count: 10, // 发送日志的数量大小，默认是10条。
  topic: 'topic', // 自定义日志主题。
  source: 'source',
  tags: {
    tags: 'tags'
  }
}
const stsOpt = {
  accessKeyId: '',
  accessKeySecret: '',
  securityToken: '',
  // 以下是一个 stsToken 刷新函数的简单示例
  refreshSTSToken: () =>
    new Promise<void>((resolve, reject) => {
      const xhr = new window.XMLHttpRequest()
      xhr.open('GET', '/sts/get_token', true)
      xhr.send()
      xhr.onreadystatechange = () => {
        if (xhr.readyState === 4) {
          if (xhr.status === 200) {
            try {
              const credential = JSON.parse(xhr.response)
              // 函数的本质目的：设置 stsOpt 的临时密钥和令牌
              stsOpt.accessKeyId = credential.AccessKeyId
              stsOpt.accessKeySecret = credential.AccessKeySecret
              stsOpt.securityToken = credential.SecurityToken
              resolve()
            } catch {
              reject('JSON.parse(xhr.response) error')
            }
          } else {
            reject('Wrong status code.')
          }
        }
      }
    })
  // refreshSTSTokenInterval: 300000,
  // stsTokenFreshTime: undefined,
}
// 隐私合规：SlsTracker 延迟实例化，用户同意隐私政策前不创建、不请求 STS、不上报
let trackerInstance: SlsTracker | null = null
const getTracker = () => {
  if (!trackerInstance) {
    const stsPlugin = createStsPlugin(stsOpt as any)
    trackerInstance = new SlsTracker(opts) // 创建SlsTracker对象
    trackerInstance.useStsPlugin(stsPlugin)
  }
  return trackerInstance
}

// 本地和测试环境不上报 SLS 日志，避免调试时被日志请求干扰
function isLocalOrTestEnv(): boolean {
  const host = window.location.host
  return (
    host.includes('localhost') || host.includes('127.0.0.1') || host.includes('test-v2.ytaxx.com')
  )
}

// 埋点调试模式：生产构建会移除 console.*（terser drop_console），调试日志统一通过
// window.console 输出以绕过移除；在控制台执行 `w.xx = true` 即可开启调试模式（线上同样可用）
let trackingDebug = false
Object.defineProperty(window, 'w', {
  configurable: true,
  get() {
    return {
      get xx() {
        return trackingDebug
      },
      set xx(value: boolean) {
        trackingDebug = !!value
        window.console.log(
          `%c[埋点] 调试模式已${trackingDebug ? '开启，后续埋点上报将在控制台输出' : '关闭'}`,
          'color: #E6A23C'
        )
      }
    }
  }
})

/** 调试模式下打印埋点日志（线上可用） */
function trackingDebugLog(payload: any) {
  if (!trackingDebug) return
  window.console.log('%c[埋点][调试]', 'color: #E6A23C', payload)
}

export const TrackingPush = (payload: any) => {
  // 未同意隐私政策时不做任何上报
  if (!hasPrivacyConsent()) return
  if (isLocalOrTestEnv()) {
    // 本地/测试环境不上报，仅在控制台打印，便于调试核对埋点内容
    window.console.log('%c[埋点][未上报-本地/测试环境]', 'color: #937AFF', payload)
    return
  }
  trackingDebugLog(payload)
  window.console.log('%c[埋点]', 'color: #67C23A', payload)
  return getTracker().send(payload)
}

class WebTracking {
  currentPath = '/'
  /** 当前页面名称（如练习页附带的科目题型），优先于路由 name 用于停留/可见性上报 */
  currentPageName = ''
  /** 当前页面进入时间，初始化为应用启动时刻，避免首个页面停留 start_time 出现 1970 */
  timeNow = Date.now()
  constructor() {
    window.onload = () => {
      // let hiddenTime = Date.now()
      let visibleTime = Date.now()
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.pushPageVisibility({
            router_path: router.currentRoute.value.path,
            page_name:
              this.currentPageName ||
              (router.currentRoute.value.meta?.trackingName as string) ||
              (router.currentRoute.value.name as string),
            eventType: 'page_visibility',
            visibility_duration: Date.now() - visibleTime,
            visibility: 'visible'
          })
          // hiddenTime = Date.now()
        } else {
          // this.pushPageVisibility({
          //   router_path: router.currentRoute.value.path,
          //   page_name: router.currentRoute.value.name as string,
          //   eventType: 'page_visibility',
          //   visibility_duration: Date.now() - hiddenTime,
          //   visibility: 'hidden'
          // })
          visibleTime = Date.now()
        }
      })
    }
  }
  click(data: ClickData) {
    if (!data.eventType) {
      data.eventType = 'common_click'
    }
    TrackingPush({
      ...this.getBasicInfo(),
      ...data
    })
  }
  pageEnter(to: RouteLocationNormalizedGeneric, from: RouteLocationNormalizedLoadedGeneric) {
    // 优先使用 meta.trackingName（埋点文档定义的页面名称），其次路由 name
    const toPageName = (to.meta?.trackingName as string) || (to.name as string)
    // beforeEach 阶段 currentRoute 仍是旧路由，page_view/page_stay 的 query/url 需从守卫参数取
    const toQuery = JSON.stringify(to.query || {})
    const toFullUrl = window.location.origin + to.fullPath
    if (this.currentPath === to.path) {
      if (this.currentPath === '/') {
        this.pushPageEnter({
          is_entry: false,
          router_path: to.path,
          page_name: toPageName,
          router_query: toQuery,
          full_url: toFullUrl,
          eventType: 'page_view',
          referrer: document.referrer
        })
        this.currentPageName = toPageName
      }
    } else {
      // 页面预览
      this.pushPageEnter({
        is_entry: false,
        router_path: to.path,
        page_name: toPageName,
        router_query: toQuery,
        full_url: toFullUrl,
        eventType: 'page_view',
        referrer: document.referrer
      })

      // 页面停留（page_name 优先使用上个页面上报的精准名称）
      // 首次进入应用时 from 为初始路由（name 为空），不存在上个页面，跳过停留上报
      if (from.name) {
        this.pushPageStayOn({
          start_time: dayjs(this.timeNow).format('YYYY-MM-DD HH:mm:ss'),
          end_time: dayjs(Date.now()).format('YYYY-MM-DD HH:mm:ss'),
          router_path: from.path,
          page_name:
            this.currentPageName || (from.meta?.trackingName as string) || (from.name as string),
          router_query: JSON.stringify(from.query || {}),
          full_url: window.location.origin + from.fullPath,
          eventType: 'page_stay',
          stay_duration: Date.now() - this.timeNow
        })
      }
      this.currentPageName = toPageName
      this.currentPath = to.path
      this.timeNow = Date.now()
    }
  }
  /** 页面浏览手动上报：供需异步获取信息的页面（如练习页等待接口返回题型）在数据就绪后调用 */
  pageEnterWithName(pageName: string) {
    this.currentPageName = pageName

    this.pushPageEnter({
      is_entry: false,
      router_path: router.currentRoute.value.path,
      page_name: pageName,
      eventType: 'page_view',
      referrer: document.referrer
    })
  }
  pushPageEnter(data: PushPageEnterData) {
    // 未同意隐私政策时不写入统计cookie
    if (hasPrivacyConsent() && !getcookie('firstPage')) {
      document.cookie = 'firstPage=true'
      data.is_entry = true
    }
    TrackingPush({
      ...this.getBasicInfo(),
      ...data
    })
  }
  pushPageStayOn(data: PushPageStayOnData) {
    TrackingPush({
      ...this.getBasicInfo(),
      ...data
    })
  }
  //页面可见性
  pushPageVisibility(data: PushPageVisibilityData) {
    TrackingPush({
      ...this.getBasicInfo(),
      ...data
    })
  }
  getBasicInfo() {
    const userStore = useUserStore()
    return {
      _arms_session: window.localStorage.getItem('_arms_session') || '',
      _arms_uid: window.localStorage.getItem('_arms_uid') || '',
      uuid: userStore?.userInfo?.uid || '',
      origin: window.location.origin,
      // 路由参数 + 完整访问 URL（pageEnter 场景会被守卫参数的准确值覆盖）
      router_query: JSON.stringify(router.currentRoute.value.query || {}),
      full_url: window.location.href,
      ...getBrowserInfo()
    }
  }
}

interface ClickData {
  eventType?: 'common_click'
  router_path: string
  page_name: string
  btn_name: string
}

interface PushPageStayOnData {
  eventType: 'page_stay'
  start_time: string
  end_time: string
  router_path: string
  page_name: string
  router_query?: string
  full_url?: string
  stay_duration: number
}

interface PushPageEnterData {
  is_entry: boolean
  eventType: 'page_view'
  router_path: string
  page_name: string
  router_query?: string
  full_url?: string
  referrer: string
}

interface PushPageVisibilityData {
  eventType: 'page_visibility'
  router_path: string
  page_name: string
  visibility_duration: number
  visibility: 'hidden' | 'visible'
}

// 获取用户设备信息
function getBrowserInfo() {
  const ua = navigator.userAgent

  // 浏览器检测
  let browser = 'Unknown'
  let version = 'Unknown'

  if (ua.includes('Chrome') && !ua.includes('Edg')) {
    browser = 'Chrome'
    const match = ua.match(/Chrome\/(\d+)/)
    version = match ? match[1] : 'Unknown'
  } else if (ua.includes('Firefox')) {
    browser = 'Firefox'
    const match = ua.match(/Firefox\/(\d+)/)
    version = match ? match[1] : 'Unknown'
  } else if (ua.includes('Safari') && !ua.includes('Chrome')) {
    browser = 'Safari'
    const match = ua.match(/Version\/(\d+).*Safari/)
    version = match ? match[1] : 'Unknown'
  } else if (ua.includes('Edg')) {
    browser = 'Edge'
    const match = ua.match(/Edg\/(\d+)/)
    version = match ? match[1] : 'Unknown'
  }

  // 操作系统检测
  let os = 'Unknown'
  if (ua.includes('Windows')) {
    os = 'Windows'
  } else if (ua.includes('Macintosh')) {
    os = 'macOS'
  } else if (ua.includes('Linux')) {
    os = 'Linux'
  } else if (ua.includes('Android')) {
    os = 'Android'
  } else if (ua.includes('iPhone') || ua.includes('iPad')) {
    os = 'iOS'
  }

  // 设备类型检测
  // let device = 'Desktop'
  // if (ua.includes('Mobile')) {
  //   device = 'Mobile'
  // } else if (ua.includes('Tablet')) {
  //   device = 'Tablet'
  // }
  // 获取本地时区偏移量（分钟）
  const timezoneOffset = new Date().getTimezoneOffset()
  const timezoneOffsetHours = -timezoneOffset / 60
  const timezoneString = `UTC${timezoneOffsetHours >= 0 ? '+' : ''}${timezoneOffsetHours}`
  const currentLocation = JSON.parse(window.localStorage.getItem('currentLocation') || '{}')
  const country = currentLocation?.location?.result?.ad_info?.nation || ''
  const province = currentLocation?.location?.result?.ad_info?.province || ''
  const city = currentLocation?.location?.result?.ad_info?.city || ''

  return {
    ua,
    browser_name: browser,
    browser_version: version,
    os_name: os,
    language: navigator.language,
    time_zone: timezoneString,
    country,
    province,
    city
  }
}

// 获取cookie
function getcookie(objname: string) {
  const arrstr = document.cookie.split('; ')
  for (let i = 0; i < arrstr.length; i++) {
    const temp = arrstr[i].split('=')
    if (temp[0] == objname) return unescape(temp[1])
  }
}

export const ytWebTracking = new WebTracking()
